// Phone numbers — one format everywhere: "+963933123456" (E.164).
// Same rules as the API (api/src/lib/phone.js); keep the two in step.

export const COUNTRIES = [
  { code: '963', flag: '🇸🇾', name: 'سوريا', len: [9], starts: '9', example: '933 123 456' },
  { code: '90', flag: '🇹🇷', name: 'تركيا', len: [10], starts: '5', example: '531 234 56 78' },
  { code: '964', flag: '🇮🇶', name: 'العراق', len: [10], starts: '7', example: '781 234 5678' },
  { code: '962', flag: '🇯🇴', name: 'الأردن', len: [9], starts: '7', example: '791 234 567' },
  { code: '961', flag: '🇱🇧', name: 'لبنان', len: [7, 8], example: '71 123 456' },
  { code: '966', flag: '🇸🇦', name: 'السعودية', len: [9], starts: '5', example: '512 345 678' },
  { code: '971', flag: '🇦🇪', name: 'الإمارات', len: [9], starts: '5', example: '501 234 567' },
  { code: '20', flag: '🇪🇬', name: 'مصر', len: [10], starts: '1', example: '101 234 5678' },
  { code: '49', flag: '🇩🇪', name: 'ألمانيا', len: [10, 11], example: '1512 3456789' },
  { code: '31', flag: '🇳🇱', name: 'هولندا', len: [9], starts: '6', example: '612 345 678' },
  { code: '46', flag: '🇸🇪', name: 'السويد', len: [9], starts: '7', example: '701 234 567' },
  { code: '1', flag: '🇺🇸', name: 'أمريكا / كندا', len: [10], example: '202 555 0123' },
];
export const DEFAULT_COUNTRY = '963';
const BY_LENGTH = [...COUNTRIES].sort((a, b) => b.code.length - a.code.length);

const clean = (s) => String(s ?? '').replace(/[\s\-().‎‏‪-‮⁦-⁩]/g, '');

/** Any way a person writes a number → "+963…", or null. */
export function normalizePhone(input) {
  let p = clean(input);
  if (!p) return null;
  if (p.startsWith('00')) p = `+${p.slice(2)}`;
  if (!p.startsWith('+')) {
    if (!/^\d+$/.test(p)) return null;
    if (/^09\d{8}$/.test(p)) p = `+963${p.slice(1)}`;
    else if (/^9\d{8}$/.test(p)) p = `+963${p}`;
    else if (/^05\d{9}$/.test(p)) p = `+90${p.slice(1)}`;
    else if (/^5\d{9}$/.test(p)) p = `+90${p}`;
    else if (/^\d{10,15}$/.test(p) && !p.startsWith('0')) p = `+${p}`;
    else return null;
  }
  if (!/^\+[1-9]\d{7,14}$/.test(p)) return null;
  return p.replace(/^\+(963|90|964|962|961|966|971)0(\d+)$/, '+$1$2');
}

/** "+963933123456" → { code: '963', local: '933123456' } (unknown code → code '' ) */
export function splitPhone(value) {
  const n = normalizePhone(value);
  if (!n) return { code: DEFAULT_COUNTRY, local: clean(value).replace(/^\+/, '') };
  const digits = n.slice(1);
  const c = BY_LENGTH.find((x) => digits.startsWith(x.code));
  return c ? { code: c.code, local: digits.slice(c.code.length) } : { code: '', local: digits };
}

/** Is this local part a real number for that country? Returns an Arabic message or ''. */
export function checkLocal(code, local) {
  if (!local) return '';
  const c = COUNTRIES.find((x) => x.code === code);
  if (!c) return /^\d{6,14}$/.test(local) ? '' : 'رقم غير صحيح';
  if (c.starts && !local.startsWith(c.starts)) return `رقم ${c.name} يبدأ بـ ${c.starts} (مثال: ${c.example})`;
  if (!c.len.includes(local.length)) return `رقم ${c.name} من ${c.len.join(' أو ')} أرقام (مثال: ${c.example})`;
  return '';
}

/** "+963933123456" → "+963 933 123 456" for display. */
export function formatPhone(value) {
  const n = normalizePhone(value);
  if (!n) return value || '';
  const { code, local } = splitPhone(n);
  if (!code) return n; // a country not in the list: shown as saved
  // 933 123 456 · 531 511 1548 · 71 123 456 — no single digit left alone.
  const groups = { 7: [3, 4], 8: [2, 3, 3], 9: [3, 3, 3], 10: [3, 3, 4], 11: [4, 3, 4] }[local.length];
  if (!groups) return `+${code} ${local}`;
  let i = 0;
  return `+${code} ${groups.map((g) => local.slice(i, (i += g))).join(' ')}`;
}

/** "+963…" → its COUNTRIES entry (known countries), null otherwise.
 *  Show the flag with <CountryFlag>: 🇸🇾 as an emoji is the OLD flag on
 *  most devices, so Syria is drawn (ui/CountryFlag.js). */
export function countryOf(value) {
  const n = normalizePhone(value);
  if (!n) return null;
  return BY_LENGTH.find((x) => n.slice(1).startsWith(x.code)) || null;
}

/** Why this typed text is not a usable phone (Arabic), or ''. Any country
 *  is accepted; for the countries we know, the length is checked too. */
export function phoneProblem(text) {
  const n = normalizePhone(text);
  if (!n) return 'اكتب الرقم مع رمز الدولة، مثال: +963 933 123 456';
  const { code, local } = splitPhone(n);
  return code ? checkLocal(code, local) : '';
}
